import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, Download, Clock } from 'lucide-react';
import * as XLSX from 'xlsx';
import BankStatementsPanel from './BankStatementsPanel';
import { SkeletonRows, NoResults, ErrorBanner } from './shell/carbonStates';

// ============================================================================
// FINANCE DASHBOARD - Cargo Handover + Bank Statements
// ============================================================================
// CHA-275 — Carbon pass (Modules concept, Orders table anatomy). Presentation
// only: same callable, same aggregation, same numbers; chrome restyled to the
// carbon-* tokens (radius 0, Plex, per-tile-border KPI strip, mono grids).

export default function FinanceDashboard() {
  const [activeTab, setActiveTab] = useState('cargo-handover');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState([]);
  const [totals, setTotals] = useState({});
  const [filters, setFilters] = useState({ divisions: [], seasons: [], companies: [], prdTypes: [] });

  // Filters
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState('SP26');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedPrdTyp, setSelectedPrdTyp] = useState(null);
  // Default to current week (Saturday-to-Friday)
  const getCurrentWeek = () => {
    const today = new Date();
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setDate(d.getDate() - 1);
    const friday = new Date(d);
    friday.setDate(friday.getDate() + 6);
    const yearStart = new Date(friday.getFullYear(), 0, 1);
    yearStart.setDate(yearStart.getDate() + 1);
    const ysDay = yearStart.getDay();
    yearStart.setDate(yearStart.getDate() - ysDay);
    yearStart.setDate(yearStart.getDate() - 1);
    const weekNum = Math.floor((d - yearStart) / (7 * 86400000)) + 1;
    return weekNum;
  };
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());

  const fmt = (v) => v == null ? '0' : new Intl.NumberFormat('en-US').format(Math.round(v));
  const fmtCur = (v) => {
    if (v == null || v === 0) return '$0';
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };
  const fmtDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'getCargoHandoverData', { timeout: 150000 });
      const result = await fn({
        division: selectedDivision,
        season: selectedSeason,
        company: selectedCompany,
        prdTyp: selectedPrdTyp
      });
      const d = result.data;
      setData(d.data || []);
      setWeeklySummary(d.weeklySummary || []);
      setTotals(d.totals || {});
      setFilters(d.filters || { divisions: [], seasons: [], companies: [], prdTypes: [] });
    } catch (err) {
      console.error('Error fetching cargo handover:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedDivision, selectedSeason, selectedCompany, selectedPrdTyp]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearFilters = () => {
    setSelectedDivision(null);
    setSelectedSeason(null);
    setSelectedCompany(null);
    setSelectedPrdTyp(null);
    setSelectedWeek(null);
  };

  const hasActiveFilters = selectedDivision || selectedSeason || selectedCompany || selectedPrdTyp || selectedWeek;

  // Client-side week filter
  const displayData = selectedWeek ? data.filter(r => Number(r.week_number) === selectedWeek) : data;
  const availableWeeks = weeklySummary.map(w => ({ num: w.weekNumber, label: w.week })).sort((a, b) => a.num - b.num);

  // Build company-grouped weekly summary from raw data
  const buildCompanySummary = () => {
    const map = {};
    data.forEach(r => {
      const wk = r.handover_week || 'Unknown';
      const co = r.Company || 'Unknown';
      const key = `${wk}|||${co}`;
      if (!map[key]) map[key] = { week: wk, weekNumber: r.week_number, weekYear: r.week_year, company: co, bookings: new Set(), issuedFob: 0, shippedFob: 0, receivedFob: 0, fobAmount: 0 };
      if (r.Booking) map[key].bookings.add(r.Booking);
      map[key].issuedFob += Number(r.issued_fob) || 0;
      map[key].shippedFob += Number(r.shipped_fob) || 0;
      map[key].receivedFob += Number(r.received_fob) || 0;
      map[key].fobAmount += Number(r.fob_amount) || 0;
    });
    return Object.values(map)
      .map(w => ({ ...w, bookingCount: w.bookings.size, bookings: undefined }))
      .sort((a, b) => {
        const wkCmp = `${a.weekYear}-${String(a.weekNumber).padStart(2, '0')}`.localeCompare(`${b.weekYear}-${String(b.weekNumber).padStart(2, '0')}`);
        return wkCmp !== 0 ? wkCmp : a.company.localeCompare(b.company);
      });
  };

  const companySummary = buildCompanySummary();
  // Group by week for rendering
  const weekGroups = {};
  companySummary.forEach(row => {
    if (!weekGroups[row.week]) weekGroups[row.week] = { week: row.week, weekNumber: row.weekNumber, weekYear: row.weekYear, rows: [], totalIssued: 0, totalShipped: 0, totalReceived: 0, totalFob: 0, totalBookings: new Set() };
    weekGroups[row.week].rows.push(row);
    weekGroups[row.week].totalIssued += row.issuedFob;
    weekGroups[row.week].totalShipped += row.shippedFob;
    weekGroups[row.week].totalReceived += row.receivedFob;
    weekGroups[row.week].totalFob += row.fobAmount;
  });
  const weekGroupList = Object.values(weekGroups).sort((a, b) =>
    `${a.weekYear}-${String(a.weekNumber).padStart(2, '0')}`.localeCompare(`${b.weekYear}-${String(b.weekNumber).padStart(2, '0')}`)
  );

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Weekly by Company sheet
    const summaryRows = companySummary.map(w => ({
      'Week': w.week,
      'Company': w.company,
      'Bookings': w.bookingCount,
      'Issued FOB': Math.round(w.issuedFob),
      'Shipped FOB': Math.round(w.shippedFob),
      'Received FOB': Math.round(w.receivedFob),
      'Total FOB': Math.round(w.fobAmount)
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'Weekly by Company');

    // Detail sheet
    const detailRows = displayData.map(r => ({
      'Week': r.handover_week || '',
      'Company': r.Company || '',
      'Division': r.DivisionName || '',
      'Booking': r.Booking || '',
      'Office': r.office || '',
      'PO Manager': r.po_manager || '',
      'PO': r.prod_num || '',
      'Line': r.open_seq || '',
      'Style': r.style || '',
      'Factory': r.factory || '',
      'Season': r.season || '',
      'Stage': r.stage || '',
      'Container': r.container || '',
      'Shipment #': r.shipment_num || '',
      'Cargo Turnover': r.Cargo_Turnover_Date || '',
      'ETD': r.ETD_POL || '',
      'ETA': r.eta_date || '',
      'Issued FOB': r.issued_fob,
      'Shipped FOB': r.shipped_fob,
      'Received FOB': r.received_fob,
      'Total FOB': r.fob_amount
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), 'Detail');

    XLSX.writeFile(wb, `Cargo_Handover_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Carbon table cell class shorthands (presentation only)
  const TH = 'px-3 py-2 font-plex-mono text-[10px] tracking-[0.12em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border';
  const MONO = 'font-plex-mono text-[11.5px]';

  return (
    <div className="font-plex text-carbon-ink space-y-5">
      {/* Tab navigation — Modules concept underline tabs */}
      <div className="flex border-b border-carbon-border">
        {[
          { key: 'cargo-handover', label: 'Cargo handover' },
          { key: 'bank-statements', label: 'Bank statements' }
        ].map((t) => {
          const on = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-[18px] py-2.5 text-[13.5px] -mb-px border-b-2 ${
                on
                  ? 'text-carbon-ink font-semibold border-carbon-blue-60'
                  : 'text-carbon-gray-50 border-transparent hover:text-carbon-ink'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'bank-statements' && <BankStatementsPanel />}

      {activeTab === 'cargo-handover' && <>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-[-0.01em] m-0">Cargo handover</h2>
          <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mt-1">
            Booked orders · FOB by turnover week &amp; company
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!loading && <span className="font-plex-mono text-[10.5px] uppercase text-carbon-gray-50">{fmt(data.length)} PO lines</span>}
          <button onClick={handleExportExcel} disabled={loading || data.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-carbon-white border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10 disabled:opacity-50">
            <Download size={12} /> Excel
          </button>
          <button onClick={fetchData} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Error — recoverable, retry re-runs the same fetch */}
      {error && !loading && (
        <ErrorBanner what="Cargo handover data failed to load." staleNote={error} onRetry={fetchData} />
      )}

      {/* KPI strip — per-tile borders (never a gray-gap grid with odd tile counts) */}
      {!loading && !error && totals && (() => {
        const filteredFOB = displayData.reduce((s, r) => s + (Number(r.fob_amount) || 0), 0);
        const filteredIssued = displayData.reduce((s, r) => s + (Number(r.issued_fob) || 0), 0);
        const filteredShipped = displayData.reduce((s, r) => s + (Number(r.shipped_fob) || 0), 0);
        const filteredReceived = displayData.reduce((s, r) => s + (Number(r.received_fob) || 0), 0);
        const filteredBookings = new Set(displayData.map(r => r.Booking).filter(Boolean)).size;
        const showFiltered = selectedWeek != null;
        const kpis = [
          { label: 'Bookings', value: fmt(showFiltered ? filteredBookings : totals.totalBookings), color: '#161616' },
          { label: 'Issued FOB', value: fmtCur(showFiltered ? filteredIssued : totals.issuedFob), color: '#0F62FE' },
          { label: 'Shipped FOB', value: fmtCur(showFiltered ? filteredShipped : totals.shippedFob), color: '#24A148' },
          { label: 'Received FOB', value: fmtCur(showFiltered ? filteredReceived : totals.receivedFob), color: '#BA4E00' },
          { label: 'Total FOB', value: fmtCur(showFiltered ? filteredFOB : totals.totalFOB), color: '#161616' }
        ];
        return (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] border-l border-b border-carbon-border">
            {kpis.map((k) => (
              <div key={k.label} className="bg-carbon-white px-4 py-3.5 border-r border-t border-carbon-border min-w-0">
                <div className="font-plex-mono text-[9.5px] tracking-[0.12em] uppercase text-carbon-gray-50">{k.label}</div>
                <div className="flex items-baseline gap-1.5 mt-1.5">
                  <span className="font-plex-mono text-xl font-semibold" style={{ color: k.color }}>{k.value}</span>
                  <span className="text-[11px] text-carbon-gray-50">{showFiltered ? `wk ${selectedWeek}` : 'all weeks'}</span>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Filter bar */}
      <div className="bg-carbon-white border border-carbon-border px-[18px] py-2.5 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 flex-wrap">
          {filters.companies && filters.companies.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">Company</span>
              <select aria-label="Company" value={selectedCompany || ''} onChange={(e) => setSelectedCompany(e.target.value || null)} className="text-xs border border-carbon-gray-30 bg-carbon-white text-carbon-ink px-2 py-1">
                <option value="">All</option>
                {filters.companies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          {filters.divisions && filters.divisions.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">Division</span>
              <select aria-label="Division" value={selectedDivision || ''} onChange={(e) => setSelectedDivision(e.target.value || null)} className="text-xs border border-carbon-gray-30 bg-carbon-white text-carbon-ink px-2 py-1">
                <option value="">All</option>
                {filters.divisions.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
          {filters.seasons && filters.seasons.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">Season</span>
              <select aria-label="Season" value={selectedSeason || ''} onChange={(e) => setSelectedSeason(e.target.value || null)} className="text-xs border border-carbon-gray-30 bg-carbon-white text-carbon-ink px-2 py-1">
                <option value="">All</option>
                {filters.seasons.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          {filters.prdTypes && filters.prdTypes.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">Prd type</span>
              <select aria-label="Prd type" value={selectedPrdTyp || ''} onChange={(e) => setSelectedPrdTyp(e.target.value || null)} className="text-xs border border-carbon-gray-30 bg-carbon-white text-carbon-ink px-2 py-1">
                <option value="">All</option>
                {filters.prdTypes.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">Week</span>
            <select aria-label="Week" value={selectedWeek || ''} onChange={(e) => setSelectedWeek(e.target.value ? Number(e.target.value) : null)} className="text-xs border border-carbon-gray-30 bg-carbon-white text-carbon-ink px-2 py-1">
              <option value="">All Weeks</option>
              {availableWeeks.map(w => <option key={w.num} value={w.num}>{w.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button onClick={clearFilters} className="font-plex-mono text-[10.5px] tracking-[0.08em] uppercase text-carbon-red-60 hover:bg-carbon-gray-10 px-2 py-1">Reset</button>
          )}
        </div>
      </div>

      {/* Weekly Summary by Company */}
      {!loading && !error && weekGroupList.length > 0 && (
        <div className="bg-carbon-white border border-carbon-border overflow-hidden">
          <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
            <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Weekly handover by company</span>
            <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">Click a week to filter</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className={`${TH} text-left`}>Week</th>
                  <th className={`${TH} text-left`}>Company</th>
                  <th className={`${TH} text-right`}>Bkgs</th>
                  <th className={`${TH} text-right`}>Issued</th>
                  <th className={`${TH} text-right`}>Shipped</th>
                  <th className={`${TH} text-right`}>Received</th>
                  <th className={`${TH} text-right`}>Total FOB</th>
                </tr>
              </thead>
              <tbody>
                {weekGroupList.map((wg, wIdx) => {
                  const isActive = selectedWeek === wg.weekNumber;
                  return (
                    <React.Fragment key={wIdx}>
                      {wg.rows.map((row, rIdx) => (
                        <tr
                          key={`${wIdx}-${rIdx}`}
                          onClick={() => setSelectedWeek(isActive ? null : wg.weekNumber)}
                          className={`border-b border-carbon-gray-10 cursor-pointer ${
                            isActive ? 'bg-carbon-blue-10' : 'hover:bg-carbon-gray-10'
                          }`}
                        >
                          {rIdx === 0 ? (
                            <td className={`px-3 py-1.5 ${MONO} font-medium text-carbon-ink align-top`} rowSpan={wg.rows.length}>{wg.week}</td>
                          ) : null}
                          <td className="px-3 py-1.5 text-[13px] text-carbon-ink">{row.company}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO} text-carbon-gray-70`}>{row.bookingCount}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO}`} style={{ color: '#0F62FE' }}>{fmtCur(row.issuedFob)}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO}`} style={{ color: '#24A148' }}>{fmtCur(row.shippedFob)}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO}`} style={{ color: '#BA4E00' }}>{fmtCur(row.receivedFob)}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO} font-semibold text-carbon-ink`}>{fmtCur(row.fobAmount)}</td>
                        </tr>
                      ))}
                      {/* Week subtotal */}
                      {wg.rows.length > 1 && (
                        <tr className={`border-b border-carbon-border ${isActive ? 'bg-carbon-blue-10' : 'bg-carbon-gray-10'}`}>
                          <td className={`px-3 py-1 font-plex-mono text-[10px] tracking-[0.08em] uppercase font-semibold text-carbon-gray-50`} colSpan={3}>Week total</td>
                          <td className={`px-3 py-1 text-right font-plex-mono text-[10px] font-semibold`} style={{ color: '#0F62FE' }}>{fmtCur(wg.totalIssued)}</td>
                          <td className={`px-3 py-1 text-right font-plex-mono text-[10px] font-semibold`} style={{ color: '#24A148' }}>{fmtCur(wg.totalShipped)}</td>
                          <td className={`px-3 py-1 text-right font-plex-mono text-[10px] font-semibold`} style={{ color: '#BA4E00' }}>{fmtCur(wg.totalReceived)}</td>
                          <td className={`px-3 py-1 text-right font-plex-mono text-[10px] font-semibold text-carbon-ink`}>{fmtCur(wg.totalFob)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-carbon-gray-10 border-t border-carbon-border">
                <tr className="font-semibold text-carbon-ink">
                  <td className="px-3 py-2 font-plex-mono text-[10px] tracking-[0.12em] uppercase" colSpan={3}>Grand total</td>
                  <td className={`px-3 py-2 text-right ${MONO} font-semibold`} style={{ color: '#0F62FE' }}>{fmtCur(totals.issuedFob)}</td>
                  <td className={`px-3 py-2 text-right ${MONO} font-semibold`} style={{ color: '#24A148' }}>{fmtCur(totals.shippedFob)}</td>
                  <td className={`px-3 py-2 text-right ${MONO} font-semibold`} style={{ color: '#BA4E00' }}>{fmtCur(totals.receivedFob)}</td>
                  <td className={`px-3 py-2 text-right ${MONO} font-semibold text-carbon-ink`}>{fmtCur(totals.totalFOB)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Booking Detail — aggregated by booking with Issued / Shipped / Received */}
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10 flex items-center gap-2">
          <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Booking detail</span>
          {selectedWeek && (
            <span className="font-plex-mono text-[10px] font-medium border border-carbon-blue-60 text-carbon-blue-60 px-1.5 py-0.5">WK {selectedWeek}</span>
          )}
        </div>

        {loading ? (
          <SkeletonRows rows={6} />
        ) : error ? (
          <div className="px-[18px] py-6 font-plex-mono text-[11px] text-carbon-gray-50">
            NO DATA — SEE THE ERROR ABOVE. RETRY RE-RUNS THE SAME FETCH.
          </div>
        ) : displayData.length === 0 ? (
          <NoResults onClear={hasActiveFilters ? clearFilters : undefined} />
        ) : (
          <div className="overflow-x-auto" style={{ maxHeight: '600px' }}>
            {(() => {
              // Aggregate by booking — collect POs and comm invoices
              const detailMap = {};
              displayData.forEach(r => {
                const bk = r.Booking || 'Unknown';
                if (!detailMap[bk]) detailMap[bk] = { Booking: bk, Company: r.Company, Cargo_Turnover_Date: r.Cargo_Turnover_Date, eta_date: null, pos: new Set(), commInvs: new Set(), containers: new Set(), shipmentNums: new Set(), issued_fob: 0, shipped_fob: 0, received_fob: 0, fob_amount: 0 };
                if (r.prod_num) detailMap[bk].pos.add(r.prod_num);
                if (r.comm_inv) detailMap[bk].commInvs.add(r.comm_inv);
                if (r.container) detailMap[bk].containers.add(r.container);
                if (r.shipment_num) detailMap[bk].shipmentNums.add(r.shipment_num);
                if (r.eta_date && !detailMap[bk].eta_date) detailMap[bk].eta_date = r.eta_date;
                detailMap[bk].issued_fob += Number(r.issued_fob) || 0;
                detailMap[bk].shipped_fob += Number(r.shipped_fob) || 0;
                detailMap[bk].received_fob += Number(r.received_fob) || 0;
                detailMap[bk].fob_amount += Number(r.fob_amount) || 0;
              });
              const detailRows = Object.values(detailMap)
                .map(r => ({ ...r, pos: [...r.pos].sort().join(', '), commInvs: [...r.commInvs].sort().join(', '), containers: [...r.containers].sort().join(', '), shipmentNums: [...r.shipmentNums].sort().join(', ') }))
                .sort((a, b) => String(a.Booking).localeCompare(String(b.Booking)));
              const totIssued = detailRows.reduce((s, r) => s + r.issued_fob, 0);
              const totShipped = detailRows.reduce((s, r) => s + r.shipped_fob, 0);
              const totReceived = detailRows.reduce((s, r) => s + r.received_fob, 0);
              const totFob = detailRows.reduce((s, r) => s + r.fob_amount, 0);
              return (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-carbon-white z-10">
                    <tr>
                      <th className={`${TH} text-left`}>Booking</th>
                      <th className={`${TH} text-left`}>PO(s)</th>
                      <th className={`${TH} text-left`}>Comm Invoice</th>
                      <th className={`${TH} text-left`}>Container</th>
                      <th className={`${TH} text-left`}>Shipment #</th>
                      <th className={`${TH} text-left`}>Company</th>
                      <th className={`${TH} text-left`}>Cargo Turnover</th>
                      <th className={`${TH} text-left`}>ETA</th>
                      <th className={`${TH} text-right`}>Issued</th>
                      <th className={`${TH} text-right`}>Shipped</th>
                      <th className={`${TH} text-right`}>Received</th>
                      <th className={`${TH} text-right`}>Total FOB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((row, idx) => (
                      <tr key={idx} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                        <td className={`px-3 py-1.5 ${MONO} font-medium text-carbon-ink`}>{row.Booking}</td>
                        <td className="px-3 py-1.5 font-plex-mono text-[10px] text-carbon-gray-70">{row.pos || '-'}</td>
                        <td className="px-3 py-1.5 font-plex-mono text-[10px] text-carbon-gray-70">{row.commInvs || '-'}</td>
                        <td className="px-3 py-1.5 font-plex-mono text-[10px] text-carbon-gray-70">{row.containers || '-'}</td>
                        <td className="px-3 py-1.5 font-plex-mono text-[10px] text-carbon-gray-70">{row.shipmentNums || '-'}</td>
                        <td className="px-3 py-1.5 text-[13px] text-carbon-ink">{row.Company}</td>
                        <td className={`px-3 py-1.5 ${MONO} text-carbon-gray-70 whitespace-nowrap`}>{fmtDate(row.Cargo_Turnover_Date)}</td>
                        <td className={`px-3 py-1.5 ${MONO} text-carbon-gray-70 whitespace-nowrap`}>{fmtDate(row.eta_date)}</td>
                        <td className={`px-3 py-1.5 text-right ${MONO}`} style={{ color: '#0F62FE' }}>{row.issued_fob ? fmtCur(row.issued_fob) : '-'}</td>
                        <td className={`px-3 py-1.5 text-right ${MONO}`} style={{ color: '#24A148' }}>{row.shipped_fob ? fmtCur(row.shipped_fob) : '-'}</td>
                        <td className={`px-3 py-1.5 text-right ${MONO}`} style={{ color: '#BA4E00' }}>{row.received_fob ? fmtCur(row.received_fob) : '-'}</td>
                        <td className={`px-3 py-1.5 text-right ${MONO} font-semibold text-carbon-ink`}>{fmtCur(row.fob_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="sticky bottom-0 bg-carbon-gray-10 border-t border-carbon-border">
                    <tr className="font-semibold text-carbon-ink">
                      <td className="px-3 py-2 font-plex-mono text-[10px] tracking-[0.12em] uppercase" colSpan={8}>Total ({fmt(detailRows.length)} bookings)</td>
                      <td className={`px-3 py-2 text-right ${MONO} font-semibold`} style={{ color: '#0F62FE' }}>{fmtCur(totIssued)}</td>
                      <td className={`px-3 py-2 text-right ${MONO} font-semibold`} style={{ color: '#24A148' }}>{fmtCur(totShipped)}</td>
                      <td className={`px-3 py-2 text-right ${MONO} font-semibold`} style={{ color: '#BA4E00' }}>{fmtCur(totReceived)}</td>
                      <td className={`px-3 py-2 text-right ${MONO} font-semibold text-carbon-ink`}>{fmtCur(totFob)}</td>
                    </tr>
                  </tfoot>
                </table>
              );
            })()}
          </div>
        )}
      </div>

      {/* Module footnote */}
      <div className="flex gap-2 items-start text-carbon-gray-50 text-xs leading-relaxed max-w-[680px]">
        <Clock size={14} className="mt-0.5 flex-shrink-0" />
        <span>Booked orders with FOB value by cargo-turnover week (Saturday to Friday). Issued / Shipped / Received reflect each PO line&rsquo;s ERP stage; the week filter applies client-side on the loaded season.</span>
      </div>
      </>}
    </div>
  );
}
